import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle, ChevronDown, ChevronRight, Circle, FlaskConical, Lock, Star } from 'lucide-react';
import { contentApi } from '../../services/contentApi';
import { CourseDetail, CourseEnrollment, CourseInterviewPackageConfig, CourseProgress, RelatedProject } from '../../types/content';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/UI/Button';
import ContentBlocks from '../../components/content/ContentBlocks';
import { CurriculumCard, moduleStripeClass, selectedTopicStripeClass, topicStripeClass } from '../../components/content/CurriculumCollapse';
import DetailAccordion from '../../components/content/DetailAccordion';
import MobileHamburgerNav from '../../components/content/MobileHamburgerNav';
import { PageLoading } from '../../components/UI/LoadingOverlay';
import { onLabCompletedMessage } from '../../utils/labCompletion';
import { moduleFlow } from '../../utils/moduleFlow';
import { courseTopicPath } from '../../utils/coursePaths';
import CourseRating, { readCourseRating } from '../../components/content/CourseRating';
import { AuthorStack } from '../../components/content/AuthorCard';
import CourseScheduleInterviews from '../../components/content/CourseScheduleInterviews';
import CourseInterviewUpgrade from '../../components/content/CourseInterviewUpgrade';
import { COURSE_IMAGES } from '../../config/courseImages';
import HeroBackdrop from '../../components/UI/HeroBackdrop';
import { heroImageFade, heroShell } from '../../content/heroThemes';

const splitLines = (value?: string | null) =>
  (value || '').split(/\r?\n/).map((line) => line.trim()).filter(Boolean);

const CourseLandingPage: React.FC = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, isProfileComplete, isLoading: authLoading } = useAuth();
  const [course, setCourse] = useState<CourseDetail | null>(null);
  const [enrollment, setEnrollment] = useState<CourseEnrollment | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [openModuleIds, setOpenModuleIds] = useState<number[]>([]);
  const [openTopicKeys, setOpenTopicKeys] = useState<string[]>([]);
  const [curriculumSeeded, setCurriculumSeeded] = useState(false);
  const [userOpenedCurriculum, setUserOpenedCurriculum] = useState(false);
  const [mobilePanel, setMobilePanel] = useState<'overview' | 'content' | ''>('');
  const [enrolling, setEnrolling] = useState(false);
  const [enrollStatus, setEnrollStatus] = useState<string | null>(null);
  const [interviewPackageCount, setInterviewPackageCount] = useState(0);
  const [interviewPackages, setInterviewPackages] = useState<CourseInterviewPackageConfig | null>(null);
  const [accessCode, setAccessCode] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [progress, setProgress] = useState<CourseProgress | null>(null);
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [ratingNote, setRatingNote] = useState('');
  const [ratingMessage, setRatingMessage] = useState<string | null>(null);

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.getElementById('course-detail-top')?.focus({ preventScroll: true });
  }, [slug, course?.id]);

  useEffect(() => {
    if (!slug) return;
    setCourse(null);
    setEnrollment(undefined);
    setEnrollStatus(null);
    setError(null);
    setCurriculumSeeded(false);
    setUserOpenedCurriculum(false);
    contentApi.getCourseBySlug(slug).then((r) => {
      const loaded = r.data.course;
      if (!loaded) {
        setError('This course is not available.');
        return;
      }
      setCourse({ ...loaded, modules: loaded.modules || [], sections: loaded.sections || [] });
    }).catch(() => setError('This course is not available.'));
  }, [slug]);

  useEffect(() => {
    if (!course?.modules?.length || curriculumSeeded) return;
    const first = course.modules[0];
    const firstKey = first.id ?? 0;
    setOpenModuleIds([firstKey]);
    const firstTopic = first.topics[0];
    setOpenTopicKeys(firstTopic ? [`${firstKey}:${firstTopic.id ?? firstTopic.slug ?? 0}`] : []);
    setCurriculumSeeded(true);
  }, [course, curriculumSeeded]);

  useEffect(() => {
    contentApi.getCourseInterviewPackages()
      .then((r) => {
        setInterviewPackages(r.data);
        if (r.data.enableInterviewsForCourseEnrollments === false) {
          setInterviewPackageCount(0);
        }
      })
      .catch(() => setInterviewPackages(null));
  }, []);

  useEffect(() => {
    if (!course || !isAuthenticated) {
      setEnrollment(isAuthenticated ? undefined : null);
      return;
    }
    const courseId = course.id;
    let cancelled = false;
    contentApi.getCourseEnrollment(courseId)
      .then((r) => {
        if (cancelled) return;
        const next = r.data.enrollment || null;
        if (next && (next.courseId !== courseId || /^cancelled$/i.test(next.status || ''))) {
          setEnrollment(null);
          return;
        }
        setEnrollment(next);
        if (next && !next.isPaid) {
          setInterviewPackageCount(next.interviewPackageCount || 0);
        }
      })
      .catch(() => {
        if (!cancelled) setEnrollment(null);
      });
    return () => { cancelled = true; };
  }, [course?.id, isAuthenticated]);

  useEffect(() => {
    if (!course || !enrollment || /^cancelled$/i.test(enrollment.status || '')) {
      setProgress(null);
      return;
    }
    contentApi.getCourseProgress(course.id).then((r) => setProgress(r.data.progress || null)).catch(() => undefined);
  }, [course?.id, enrollment?.id, enrollment?.status]);

  useEffect(() => {
    if (!course?.id || !enrollment || /^cancelled$/i.test(enrollment.status || '')) return undefined;
    return onLabCompletedMessage((_, courseId) => {
      if (!courseId || courseId === course.id) {
        contentApi.getCourseProgress(course.id).then((r) => setProgress(r.data.progress || null)).catch(() => undefined);
      }
    });
  }, [course?.id, enrollment?.id, enrollment?.status]);

  const outcomes = useMemo(() => splitLines(course?.learningOutcomes), [course]);
  const firstTopic = course?.modules?.flatMap((m) => m.topics)[0];
  const price = Number(course?.price || 0);
  const discount = Number(course?.discountPercent || 0);

  const enrollmentForCourse = course && enrollment && enrollment.courseId === course.id && !/^cancelled$/i.test(enrollment.status || '')
    ? enrollment
    : null;
  const isEnrolled = !!enrollmentForCourse;

  const continueLearning = (target?: CourseEnrollment | null) => {
    const topicSlug = target?.firstTopicSlug || firstTopic?.slug;
    if (course && topicSlug) navigate(`/courses/${course.slug}/topics/${topicSlug}`);
  };

  const continueLearningButton = (opts?: { fullWidth?: boolean; variant?: 'primary' | 'onDark' }) => (
    <Button
      variant={opts?.variant ?? 'primary'}
      fullWidth={opts?.fullWidth ?? false}
      size="lg"
      onClick={() => continueLearning(enrollmentForCourse)}
    >
      Continue learning
      <ArrowRight className="ml-2 h-5 w-5" />
    </Button>
  );

  const handleEnroll = async () => {
    if (!course) return;
    if (!isAuthenticated) {
      navigate('/login', {
        state: {
          message: `Please sign in to enroll in ${course.title}.`,
          from: location.pathname,
        },
      });
      return;
    }
    if (!isProfileComplete) {
      navigate('/profile-setup');
      return;
    }
    if (!user?.isEmailVerified) {
      navigate('/verify-email');
      return;
    }

    setEnrolling(true);
    setEnrollStatus(null);
    try {
      const response = await contentApi.enrollCourse(course.id, interviewPackageCount);
      const next = response.data.enrollment;
      if (!next) {
        setEnrollStatus(response.data.message || 'Enrollment could not be completed.');
        return;
      }
      setEnrollment(next);
      if (course.maxSeats && course.maxSeats > 0) {
        setCourse((current) => {
          if (!current) return current;
          const enrollmentCount = (current.enrollmentCount || 0) + 1;
          const seatsRemaining = Math.max(0, current.maxSeats! - enrollmentCount);
          return { ...current, enrollmentCount, seatsRemaining, isFull: seatsRemaining <= 0 };
        });
      }
      if (next.isPaid) continueLearning(next);
      else navigate(`/courses/${course.slug}/payment`);
    } catch (err: unknown) {
      setEnrollStatus(err instanceof Error ? err.message : 'Enrollment could not be completed. Please try again.');
    } finally {
      setEnrolling(false);
    }
  };

  const handleRedeem = async () => {
    if (!course || !accessCode.trim()) return;
    if (!isAuthenticated) {
      navigate('/login', { state: { from: location.pathname, message: 'Sign in with the invited email to redeem the code.' } });
      return;
    }
    setRedeeming(true);
    setEnrollStatus(null);
    try {
      const response = await contentApi.redeemCourseCode({
        email: user?.email,
        code: accessCode.trim(),
        courseId: course.id,
      });
      const next = response.data.enrollment;
      if (!next) {
        setEnrollStatus(response.data.message || 'This code could not be redeemed.');
        return;
      }
      setEnrollment(next);
      setAccessCode('');
      if (next.isPaid) continueLearning(next);
    } catch (err: unknown) {
      setEnrollStatus(err instanceof Error ? err.message : 'This code could not be redeemed.');
    } finally {
      setRedeeming(false);
    }
  };

  const renderEnrollActions = (opts?: { fullWidth?: boolean; variant?: 'primary' | 'onDark'; includePackages?: boolean }) => {
    const fullWidth = opts?.fullWidth ?? false;
    const variant = opts?.variant ?? 'primary';
    const includePackages = opts?.includePackages ?? false;
    if (!course) return null;

    const checkingEnrollment = isAuthenticated && (enrollment === undefined || (!!enrollment && enrollment.courseId !== course.id));
    const isPaid = !!enrollmentForCourse?.isPaid;

    if (!checkingEnrollment && isPaid) {
      return (
        <div className="space-y-3">
          <div className={`flex items-start gap-2 text-sm font-medium ${variant === 'onDark' ? 'text-emerald-200' : 'text-emerald-700'}`}>
            <CheckCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>You are enrolled.</span>
          </div>
          {continueLearningButton({ fullWidth, variant })}
        </div>
      );
    }

    if (!checkingEnrollment && enrollmentForCourse && !enrollmentForCourse.isPaid) {
      const selectedPackage = interviewPackages?.packages?.find((p) => p.count === interviewPackageCount);
      const coursePayable = Math.max(0, Math.round(price * (1 - Math.min(100, Math.max(0, discount)) / 100)));
      const packageFee = selectedPackage?.totalPrice ?? 0;
      const total = coursePayable + packageFee;
      return (
        <div className="space-y-4">
          <p className={`text-sm ${variant === 'onDark' ? 'text-amber-100' : 'text-amber-800'}`}>
            Enrollment started — choose a review package if you want, then complete payment.
          </p>
          {includePackages && (
            <fieldset className={`space-y-2 text-left ${variant === 'onDark' ? 'text-primary-50' : 'text-gray-800'}`}>
              <legend className="sr-only">Review session package</legend>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="interview-package-pending"
                  className="mt-1"
                  checked={interviewPackageCount === 0}
                  onChange={() => setInterviewPackageCount(0)}
                />
                <span>Self-paced only — no review sessions</span>
              </label>
              {(interviewPackages?.packages || []).map((tier) => (
                <label key={tier.count} className="flex items-start gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="interview-package-pending"
                    className="mt-1"
                    checked={interviewPackageCount === tier.count}
                    onChange={() => setInterviewPackageCount(tier.count)}
                  />
                  <span>
                    {tier.count} review session{tier.count === 1 ? '' : 's'} — ₹{tier.totalPrice.toLocaleString('en-IN')}
                    <span className={variant === 'onDark' ? 'text-primary-200' : 'text-gray-500'}> (₹{tier.perSessionPrice.toLocaleString('en-IN')} each)</span>
                  </span>
                </label>
              ))}
              <p className={`text-sm pt-1 ${variant === 'onDark' ? 'text-primary-100' : 'text-gray-700'}`}>
                Estimated total: <span className="font-semibold">₹{total.toLocaleString('en-IN')}</span>
                {packageFee > 0 ? ` (course ₹${coursePayable.toLocaleString('en-IN')} + reviews ₹${packageFee.toLocaleString('en-IN')})` : null}
              </p>
            </fieldset>
          )}
          <Button
            variant={variant}
            fullWidth={fullWidth}
            size="lg"
            disabled={enrolling}
            onClick={async () => {
              if (!course) return;
              setEnrolling(true);
              setEnrollStatus(null);
              try {
                const response = await contentApi.enrollCourse(course.id, interviewPackageCount);
                const next = response.data.enrollment;
                if (!next) {
                  setEnrollStatus(response.data.message || 'Could not update enrollment.');
                  return;
                }
                setEnrollment(next);
                if (next.isPaid) continueLearning(next);
                else navigate(`/courses/${course.slug}/payment`);
              } catch (err: unknown) {
                setEnrollStatus(err instanceof Error ? err.message : 'Could not update enrollment.');
              } finally {
                setEnrolling(false);
              }
            }}
          >
            {enrolling ? 'Updating…' : 'Complete payment'}
            {!enrolling && <ArrowRight className="ml-2 h-5 w-5" />}
          </Button>
        </div>
      );
    }

    if (authLoading || checkingEnrollment) {
      return (
        <Button variant={variant} fullWidth={fullWidth} size="lg" disabled>
          Enroll now
        </Button>
      );
    }

    if (isAuthenticated && !isProfileComplete) {
      return (
        <div className="space-y-2">
          <p className={`text-sm ${variant === 'onDark' ? 'text-amber-100' : 'text-amber-700'}`}>
            Complete your profile before you can enroll.
          </p>
          <Link to="/profile-setup" className={fullWidth ? 'block' : 'inline-block'}>
            <Button variant={variant} fullWidth={fullWidth} size="lg">
              Complete profile to enroll
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      );
    }

    if (isAuthenticated && !user?.isEmailVerified) {
      return (
        <div className="space-y-2">
          <p className={`text-sm ${variant === 'onDark' ? 'text-amber-100' : 'text-amber-700'}`}>
            Verify your email before you can enroll.
          </p>
          <Link to="/verify-email" className={fullWidth ? 'block' : 'inline-block'}>
            <Button variant={variant} fullWidth={fullWidth} size="lg">
              Verify email to enroll
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      );
    }

    if (isAuthenticated) {
      const selectedPackage = interviewPackages?.packages?.find((p) => p.count === interviewPackageCount);
      const coursePayable = Math.max(0, Math.round(price * (1 - Math.min(100, Math.max(0, discount)) / 100)));
      const packageFee = selectedPackage?.totalPrice ?? 0;
      const total = coursePayable + packageFee;
      return (
        <div className="space-y-4">
          {includePackages && (
            <fieldset className="space-y-2 text-left">
              <legend className="sr-only">Review session package</legend>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="interview-package"
                  className="mt-1"
                  checked={interviewPackageCount === 0}
                  onChange={() => setInterviewPackageCount(0)}
                />
                <span>Self-paced only — no review sessions</span>
              </label>
              {(interviewPackages?.packages || []).map((tier) => (
                <label key={tier.count} className="flex items-start gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="interview-package"
                    className="mt-1"
                    checked={interviewPackageCount === tier.count}
                    onChange={() => setInterviewPackageCount(tier.count)}
                  />
                  <span>
                    {tier.count} review session{tier.count === 1 ? '' : 's'} — ₹{tier.totalPrice.toLocaleString('en-IN')}
                    <span className="text-gray-500"> (₹{tier.perSessionPrice.toLocaleString('en-IN')} each)</span>
                  </span>
                </label>
              ))}
              <p className="text-sm text-gray-700 pt-1">
                Estimated total: <span className="font-semibold">₹{total.toLocaleString('en-IN')}</span>
                {packageFee > 0 ? ` (course ₹${coursePayable.toLocaleString('en-IN')} + reviews ₹${packageFee.toLocaleString('en-IN')})` : null}
              </p>
            </fieldset>
          )}
          <Button variant={variant} fullWidth={fullWidth} size="lg" onClick={handleEnroll} disabled={enrolling}>
            {enrolling ? 'Enrolling…' : 'Enroll now'}
            {!enrolling && <ArrowRight className="ml-2 h-5 w-5" />}
          </Button>
        </div>
      );
    }

    return (
      <Link to="/login" state={{ from: location.pathname }} className={fullWidth ? 'block' : 'inline-block'}>
        <Button variant={variant} fullWidth={fullWidth} size="lg">
          Sign in to enroll
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </Link>
    );
  };

  const renderCodeEntry = (opts?: { onDark?: boolean }) => (
    <div className="mt-4 space-y-2">
      <p className={`text-xs ${opts?.onDark ? 'text-primary-100' : 'text-gray-500'}`}>Have an access code? Redeem it with the invited email — no payment needed.</p>
      <div className="flex gap-2">
        <input
          value={accessCode}
          onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
          placeholder="Access code"
          className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900"
        />
        <Button type="button" size="sm" variant={opts?.onDark ? 'onDark' : 'outline'} onClick={handleRedeem} disabled={redeeming || !accessCode.trim()}>
          {redeeming ? 'Checking…' : 'Redeem'}
        </Button>
      </div>
    </div>
  );

  if (error) return <div className="max-w-3xl mx-auto px-4 py-16 text-gray-600">{error}</div>;
  if (!course) return <PageLoading label="Loading course…" />;

  const hasCurriculum = (course.modules || []).length > 0;
  const moduleKey = (module: { id?: number }, index: number) => module.id ?? index;
  const topicKey = (modKey: number, topic: { id?: number; slug?: string }, index: number) =>
    `${modKey}:${topic.id ?? topic.slug ?? index}`;
  const isModuleOpen = (key: number) => openModuleIds.includes(key);
  const isTopicOpen = (key: string) => openTopicKeys.includes(key);
  const toggleModule = (module: { id?: number; topics: { id?: number; slug?: string }[] }, index: number) => {
    setUserOpenedCurriculum(true);
    const key = moduleKey(module, index);
    if (openModuleIds.includes(key)) {
      setOpenModuleIds([]);
      setOpenTopicKeys([]);
      return;
    }
    setOpenModuleIds([key]);
    const firstTopic = module.topics[0];
    setOpenTopicKeys(firstTopic ? [topicKey(key, firstTopic, 0)] : []);
  };
  const toggleTopic = (key: string) => {
    setUserOpenedCurriculum(true);
    setOpenTopicKeys((current) => (current.length === 1 && current[0] === key ? [] : [key]));
  };
  const canOpenTopics = isEnrolled;
  const linkedProjects = (course.projects || []) as RelatedProject[];

  return (
    <div id="course-detail-top" tabIndex={-1} className="outline-none">
      <header className={heroShell('courses')}>
        <HeroBackdrop tone="courses" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-[1.485rem] md:py-[1.733rem] lg:py-[1.98rem]">
          <div className="grid lg:grid-cols-2 gap-[1.238rem] lg:gap-[1.98rem] items-center">
            <div>
              <Link to="/courses" className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-100 hover:text-white">
                <ArrowLeft className="h-4 w-4" />
                Back to courses
              </Link>
              <p className="mt-3 text-xs font-semibold uppercase tracking-[0.18em] text-primary-200">
                {course.categoryName || 'Course'}
              </p>
              <h1 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight">{course.title}</h1>
              {course.subtitle && (
                <p className="mt-4 text-sm sm:text-base text-primary-100/90 leading-relaxed max-w-xl">{course.subtitle}</p>
              )}
              <div className="mt-4">
                <CourseRating
                  average={readCourseRating(course).average}
                  count={readCourseRating(course).count}
                  className="text-sm text-amber-200"
                  showEmpty
                />
              </div>
              <div className="mt-4 flex flex-wrap gap-x-3 gap-y-1 text-sm text-primary-100/90">
                <span>{course.difficulty}</span>
                {course.estimatedDurationHours ? <span>· {course.estimatedDurationHours}+ hours</span> : null}
                <span>· {course.moduleCount ?? course.modules.length} modules · {course.topicCount ?? 0} topics{course.labCount ? ` · ${course.labCount} labs` : ''}</span>
                {course.maxSeats && course.maxSeats > 0 ? (
                  <span>· {course.seatsRemaining ?? Math.max(0, course.maxSeats - (course.enrollmentCount || 0))} seats left</span>
                ) : null}
              </div>
            </div>
            <div className="relative">
              <div className="relative overflow-hidden rounded-xl shadow-2xl ring-1 ring-white/10">
                <img
                  src={course.coverImageUrl || COURSE_IMAGES.coursesHero}
                  alt=""
                  className="w-full h-[13.86rem] sm:h-[16.83rem] lg:h-[19.8rem] object-cover object-center"
                />
                <div className={heroImageFade('courses')} />
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 space-y-12">
        {course.author?.displayName || (course.authors && course.authors.length) ? (
          <AuthorStack authors={course.authors?.length ? course.authors : [course.author]} />
        ) : null}
        {isEnrolled && (
          <section className="max-w-3xl">
            {continueLearningButton()}
          </section>
        )}

        {!enrollmentForCourse?.isPaid && (
          <section className="rounded-2xl border border-primary-100 bg-white/70 backdrop-blur-sm p-6 max-w-3xl">
            <h2 className="text-2xl font-bold text-gray-900">{interviewPackages?.upsellTitle || 'Add professional review sessions'}</h2>
            <p className="mt-3 text-gray-700 leading-relaxed">
              {interviewPackages?.upsellSummary
                || 'Pair your self-paced course with 1:1 reviews from industry professionals. They help you find skill gaps early, build a realistic plan, and stay aligned with how teams hire and ship software.'}
            </p>
            {interviewPackages?.enableInterviewsForCourseEnrollments !== false && interviewPackages?.scheduleFirstTip ? (
              <p className="mt-3 text-sm font-medium text-amber-900">{interviewPackages.scheduleFirstTip}</p>
            ) : null}
            {course.isFull && !enrollmentForCourse ? (
              <div className="mt-5 space-y-3">
                <p className="text-sm font-semibold text-amber-900">Seats are full for this course.</p>
                <Button
                  fullWidth
                  disabled={enrolling}
                  onClick={async () => {
                    if (!isAuthenticated) {
                      navigate('/login', { state: { from: location.pathname } });
                      return;
                    }
                    setEnrolling(true);
                    setEnrollStatus(null);
                    try {
                      const response = await contentApi.registerCourseInterest(course.id);
                      setEnrollStatus(response.data.message || 'You are on the interest list.');
                    } catch (err: unknown) {
                      setEnrollStatus(err instanceof Error ? err.message : 'Could not join the interest list.');
                    } finally {
                      setEnrolling(false);
                    }
                  }}
                >
                  {enrolling ? 'Submitting…' : 'Notify me when available'}
                </Button>
              </div>
            ) : (
              <div className="mt-5 max-w-xl space-y-4">
                {renderEnrollActions({ fullWidth: true, includePackages: interviewPackages?.enableInterviewsForCourseEnrollments !== false })}
                {renderCodeEntry()}
              </div>
            )}
            {enrollStatus && <p className="mt-3 text-sm text-error-600">{enrollStatus}</p>}
          </section>
        )}

        {isEnrolled && enrollmentForCourse && (
          <section className="max-w-3xl space-y-3">
            {(enrollmentForCourse.interviewPackageCount || 0) > 0 && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Your professional review sessions</h2>
                <p className="mt-2 text-gray-700 leading-relaxed">
                  You opted for {enrollmentForCourse.interviewPackageCount} review session
                  {(enrollmentForCourse.interviewPackageCount || 0) === 1 ? '' : 's'} with this course.
                  Schedule them from here or while learning.
                </p>
              </div>
            )}
            <CourseScheduleInterviews
              courseId={course.id}
              courseSlug={course.slug}
              courseTitle={course.title}
              interviewPackageCount={enrollmentForCourse.interviewPackageCount || 0}
            />
            {enrollmentForCourse.isPaid ? (
              <CourseInterviewUpgrade
                courseId={course.id}
                enrollment={enrollmentForCourse}
                packages={interviewPackages}
                onUpgraded={(next, message) => {
                  setEnrollment(next);
                  if (message) setEnrollStatus(message);
                }}
              />
            ) : null}
          </section>
        )}

        {course.summary && (
          <p className="hidden lg:block text-lg text-gray-700 leading-relaxed max-w-3xl">{course.summary}</p>
        )}

        {outcomes.length > 0 && (
          <section className="hidden lg:block">
            <h2 className="text-2xl font-bold text-gray-900">What you&apos;ll learn</h2>
            <ul className="mt-4 grid md:grid-cols-2 gap-3">
              {outcomes.map((line) => (
                <li key={line} className="flex gap-2 text-gray-700">
                  <span className="text-primary-600 font-semibold">✓</span>
                  {line}
                </li>
              ))}
            </ul>
          </section>
        )}

        {splitLines(course.prerequisites).length > 0 && (
          <section className="hidden lg:block">
            <h2 className="text-xl font-semibold text-gray-900">Prerequisites</h2>
            <p className="mt-2 text-gray-600 whitespace-pre-wrap">{course.prerequisites}</p>
          </section>
        )}

        {(course.summary || outcomes.length > 0 || splitLines(course.prerequisites).length > 0) && (
          <div className="lg:hidden">
            <DetailAccordion
              title="Overview"
              open={mobilePanel === 'overview'}
              onToggle={() => setMobilePanel((current) => current === 'overview' ? '' : 'overview')}
            >
              {course.summary && <p className="text-gray-700 leading-relaxed">{course.summary}</p>}
              {outcomes.length > 0 && (
                <ul className="mt-4 space-y-2">
                  {outcomes.map((line) => (
                    <li key={line} className="flex gap-2 text-gray-700">
                      <span className="text-primary-600 font-semibold">✓</span>
                      {line}
                    </li>
                  ))}
                </ul>
              )}
              {splitLines(course.prerequisites).length > 0 && (
                <div className="mt-4">
                  <p className="font-semibold text-gray-900">Prerequisites</p>
                  <p className="mt-2 text-gray-600 whitespace-pre-wrap">{course.prerequisites}</p>
                </div>
              )}
            </DetailAccordion>
          </div>
        )}

        <section>
          <div className="hidden lg:flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-2xl font-bold text-gray-900">Course content</h2>
            {canOpenTopics && progress && (
              <p className="text-sm text-gray-600">
                {progress.isCourseCompleted
                  ? 'Course completed'
                  : `${progress.completedTopicCount}/${progress.topicCount} topics · ${progress.completedLabCount}/${progress.labCount} labs · ${progress.completedAssessmentCount || 0}/${progress.assessmentCount || 0} assessments`}
              </p>
            )}
          </div>
          <div className="lg:hidden">
            <MobileHamburgerNav
              open={mobilePanel === 'content'}
              onToggle={() => setMobilePanel((current) => current === 'content' ? '' : 'content')}
              label="table of contents"
            />
          </div>
          <div className={`${mobilePanel === 'content' ? 'block' : 'hidden'} lg:block`}>
          {!hasCurriculum && (course.sections || []).length === 0 && (
            <p className="mt-4 text-gray-500">Curriculum for this track is being assembled.</p>
          )}

          {hasCurriculum && (
            <div className="mt-5 space-y-4">
              {course.modules.map((module, index) => {
                const modKey = moduleKey(module, index);
                const moduleOpen = isModuleOpen(modKey);
                const stripe = moduleStripeClass(index);
                const moduleDone = !!(module.id && progress?.modules.some((item) => item.id === module.id && item.isCompleted));
                return (
                      <CurriculumCard key={modKey} open={moduleOpen} includeNeighbors scrollOnOpen={userOpenedCurriculum} className={stripe.box}>
                    <div className="flex items-stretch">
                      <button
                        type="button"
                        aria-expanded={moduleOpen}
                        className={`flex-1 text-left px-5 py-4 flex items-start gap-3 ${stripe.hover}`}
                        onClick={() => toggleModule(module, index)}
                      >
                        {moduleOpen
                          ? <ChevronDown className={`h-5 w-5 mt-0.5 shrink-0 ${stripe.chevron}`} />
                          : <ChevronRight className={`h-5 w-5 mt-0.5 shrink-0 ${stripe.chevron}`} />}
                        {moduleDone
                          ? <CheckCircle className="h-5 w-5 mt-0.5 text-success-500 shrink-0" />
                          : null}
                        <div className="flex-1 min-w-0">
                          <p className={`font-semibold ${stripe.title}`}>{String(index + 1).padStart(2, '0')} {module.title}</p>
                          {module.author?.displayName ? (
                            <p className={`text-xs mt-1 ${stripe.muted}`}>By {module.author.displayName}</p>
                          ) : null}
                          <p className={`text-sm mt-1 ${stripe.muted}`}>
                            {module.topics.length} topics · {module.topics.reduce((n, t) => n + (t.relatedLabs?.length || 0), 0)} labs
                            {(module.assessments || []).length ? ` · ${(module.assessments || []).length} assessment` : ''}
                          </p>
                        </div>
                      </button>
                    </div>
                    {moduleOpen && (
                      <div className="px-3 pb-4 space-y-2">
                        {module.goal && <p className={`text-sm px-2 pb-1 ${stripe.muted}`}>{module.goal}</p>}
                        {moduleFlow(module.topics, module.assessments).map((item, fi) => {
                          if (item.kind === 'assessment') {
                            const assessment = item.assessment;
                            const passed = !!(assessment.id && (progress?.passedAssessmentIds || []).includes(assessment.id));
                            return (
                              <div key={assessment.id || `${assessment.title}-${fi}`} className="mx-2 mt-2 rounded-xl border border-primary-200 bg-white px-3 py-2 flex items-center justify-between gap-3">
                                <span className="text-sm font-medium text-gray-900">
                                  {passed ? <CheckCircle className="inline h-4 w-4 text-success-500 mr-1" /> : null}
                                  Assessment: {assessment.title}
                                </span>
                                {canOpenTopics && assessment.id ? (
                                  <Link to={`/courses/${course.slug}/assessments/${assessment.id}`} onClick={() => setMobilePanel('')} className="text-xs font-semibold text-primary-700">
                                    {passed ? 'Review' : 'Take'}
                                  </Link>
                                ) : (
                                  <Lock className="h-3.5 w-3.5 text-gray-400" />
                                )}
                              </div>
                            );
                          }

                          const topic = item.topic;
                          const ti = item.topicIndex;
                          const tKey = topicKey(modKey, topic, ti);
                          const topicOpen = isTopicOpen(tKey);
                          const topicStripe = topicOpen ? selectedTopicStripeClass() : topicStripeClass(index, ti);
                          const hasDetail = !!(topic.objective || (topic.relatedLabs || []).length);
                          const topicDone = !!(topic.id && progress?.completedTopicIds?.includes(topic.id));
                          return (
                            <CurriculumCard key={tKey} open={topicOpen} delayMs={80} includeNeighbors scrollOnOpen={userOpenedCurriculum} className={topicStripe.box}>
                              <div className="flex items-stretch">
                                <button
                                  type="button"
                                  aria-expanded={topicOpen}
                                  className={`flex-1 text-left px-3 py-2.5 flex items-start gap-2 ${topicStripe.hover}`}
                                  onClick={() => toggleTopic(tKey)}
                                >
                                  {topicOpen
                                    ? <ChevronDown className={`h-4 w-4 mt-0.5 shrink-0 ${topicStripe.chevron}`} />
                                    : <ChevronRight className={`h-4 w-4 mt-0.5 shrink-0 ${topicStripe.chevron}`} />}
                                  <span className="flex-1 min-w-0">
                                    <span className={`text-sm font-medium inline-flex items-center gap-2 ${topicStripe.title}`}>
                                      {!canOpenTopics && <Lock className={`h-3.5 w-3.5 shrink-0 ${topicStripe.muted}`} />}
                                      {topicDone
                                        ? <CheckCircle className="h-4 w-4 text-success-500 shrink-0" />
                                        : canOpenTopics ? <Circle className={`h-4 w-4 shrink-0 ${topicStripe.muted}`} /> : null}
                                      {topic.title}
                                    </span>
                                    <span className={`block text-xs mt-0.5 ${topicStripe.muted}`}>{topic.estimatedMinutes} min</span>
                                  </span>
                                </button>
                                {canOpenTopics && topic.slug ? (
                                  <Link
                                    to={courseTopicPath(course.slug, topic.slug, module.id)}
                                    onClick={() => setMobilePanel('')}
                                    className={`shrink-0 px-3 py-2.5 text-xs font-semibold self-center ${topicStripe.chevron}`}
                                  >
                                    Open
                                  </Link>
                                ) : null}
                              </div>
                              {topicOpen && (
                                <div className="px-3 pb-3 pt-2 border-t border-gray-100 bg-white space-y-2">
                                  {topic.objective
                                    ? <p className="text-sm text-gray-700">{topic.objective}</p>
                                    : !hasDetail
                                      ? <p className="text-sm text-gray-500">No extra detail for this topic.</p>
                                      : null}
                                  {(topic.relatedLabs || []).map((lab) => {
                                    const done = !!progress?.completedLabIds?.includes(lab.id);
                                    return (
                                      <Link key={lab.id} to={lab.slug ? `/labs/${lab.slug}?course=${course.slug}` : '/labs'} className="flex items-center gap-2 text-sm text-primary-700 font-medium">
                                        {done ? <CheckCircle className="h-3.5 w-3.5 shrink-0 text-success-500" /> : <FlaskConical className="h-3.5 w-3.5 shrink-0" />}
                                        {lab.name}{done ? ' · Completed' : ''}
                                      </Link>
                                    );
                                  })}
                                </div>
                              )}
                            </CurriculumCard>
                          );
                        })}
                      </div>
                    )}
                  </CurriculumCard>
                );
              })}
            </div>
          )}

          {!hasCurriculum && (course.sections || []).map((section) => (
            <section key={section.id} className="mt-8">
              <h3 className="text-xl font-semibold">{section.title}</h3>
              <div className="mt-3"><ContentBlocks blocks={section.blocks} /></div>
            </section>
          ))}
          </div>
        </section>

        {linkedProjects.length > 0 && (
          <section>
            <h2 className="text-2xl font-bold text-gray-900">Related projects</h2>
            <p className="mt-2 text-sm text-gray-600">Optional self-paced practice. Completing a project is not required to finish this course.</p>
            <div className="mt-5 grid md:grid-cols-2 gap-4">
              {linkedProjects.map((project) => (
                <Link key={project.id} to={`/projects/${project.slug}`} className="surface-card surface-card-hover overflow-hidden">
                  <img src={project.coverImageUrl || COURSE_IMAGES.projectsHero} alt="" className="h-36 w-full object-cover" />
                  <div className="p-4">
                    <p className="font-semibold text-gray-900">{project.title}</p>
                    {project.subtitle && <p className="text-sm text-gray-600 mt-1">{project.subtitle}</p>}
                    <p className="text-xs text-gray-500 mt-2">{project.difficulty}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <Link to="/programs" className="block surface-card surface-card-hover p-5">
          <p className="font-semibold text-gray-900">Prefer a guided cohort? Explore IDC Academy programs</p>
        </Link>

        <section className="max-w-xl surface-card p-5 ml-auto">
          <h2 className="text-lg font-semibold text-gray-900">Rate this course</h2>
          <p className="mt-1 text-sm text-gray-600">
            <CourseRating
              average={readCourseRating(course).average}
              count={readCourseRating(course).count}
              className="text-sm text-amber-700"
              showEmpty
            />
            <span className="ml-2 text-gray-500">Ratings are visible to everyone browsing the catalog.</span>
          </p>
          {isAuthenticated ? (
            <form
              className="mt-3 flex flex-col sm:flex-row gap-3 sm:items-end"
              onSubmit={async (event) => {
                event.preventDefault();
                setRatingMessage(null);
                try {
                  const response = await contentApi.rateCourse(course.id, rating, ratingNote);
                  const next = readCourseRating(response.data);
                  setCourse((current) => current ? { ...current, averageRating: next.average, ratingCount: next.count } : current);
                  setRatingMessage(response.data.message || 'Rating saved.');
                } catch (err: unknown) {
                  setRatingMessage(err instanceof Error ? err.message : 'Could not save the rating.');
                }
              }}
            >
              <div>
                <p className="text-sm text-gray-700 mb-1">Your rating</p>
                <div
                  className="flex items-center gap-0.5"
                  role="radiogroup"
                  aria-label="Course rating"
                  onMouseLeave={() => setHoverRating(0)}
                >
                  {[1, 2, 3, 4, 5].map((value) => {
                    const filled = value <= (hoverRating || rating);
                    return (
                      <button
                        key={value}
                        type="button"
                        role="radio"
                        aria-checked={rating === value}
                        aria-label={`${value} star${value === 1 ? '' : 's'}`}
                        className="p-0.5 rounded text-amber-400 hover:text-amber-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
                        onMouseEnter={() => setHoverRating(value)}
                        onFocus={() => setHoverRating(value)}
                        onBlur={() => setHoverRating(0)}
                        onClick={() => setRating(value)}
                      >
                        <Star className={`h-6 w-6 ${filled ? 'fill-current' : ''}`} strokeWidth={1.75} />
                      </button>
                    );
                  })}
                </div>
              </div>
              <label className="text-sm text-gray-700 flex-1">
                Comment (optional)
                <input className="mt-1 w-full rounded-md border-gray-300" value={ratingNote} onChange={(e) => setRatingNote(e.target.value)} />
              </label>
              <Button type="submit" size="sm">Save rating</Button>
            </form>
          ) : (
            <Link to="/login" state={{ from: location.pathname }} className="mt-3 inline-block text-sm font-semibold text-primary-700">Sign in to rate this course</Link>
          )}
          {ratingMessage && <p className="mt-2 text-sm text-gray-600">{ratingMessage}</p>}
        </section>
      </div>
    </div>
  );
};

export default CourseLandingPage;
